import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { House, Barbell, ForkKnife, Snowflake, Target, ChartLine, UsersThree, CreditCard, SignOut, User, Trophy, Heartbeat, EnvelopeSimple } from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House },
  { to: "/gym-plan", label: "Gym Plan", icon: Barbell },
  { to: "/meal-plan", label: "Meal Plan", icon: ForkKnife },
  { to: "/readiness", label: "Readiness", icon: Heartbeat },
  { to: "/recovery", label: "Recovery", icon: Snowflake },
  { to: "/goals", label: "Goals", icon: Target },
  { to: "/progress", label: "Progress", icon: ChartLine },
  { to: "/matches", label: "Matches", icon: Trophy },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const isCoach = user?.role === "coach";

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  const links = isCoach
    ? [
        { to: "/coach", label: "Players", icon: UsersThree },
        { to: "/coach/digest", label: "Digest", icon: EnvelopeSimple },
        { to: "/team", label: "Team", icon: UsersThree },
        { to: "/pricing", label: "Subscription", icon: CreditCard },
      ]
    : [...NAV, { to: "/team", label: "Team", icon: UsersThree }];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <aside className="w-64 border-r border-[#262626] flex flex-col sticky top-0 h-screen">
        <Link to="/" className="px-6 py-6 border-b border-[#262626] flex items-baseline gap-2" data-testid="sidebar-logo">
          <span className="font-heading font-black text-2xl uppercase tracking-tight">Gainline</span>
          <span className="font-heading font-black text-2xl text-[#FF3B30] uppercase">Performance</span>
        </Link>
        <nav className="flex-1 py-4">
          {links.map((l) => {
            const Icon = l.icon;
            const active = loc.pathname === l.to || loc.pathname.startsWith(l.to + "/");
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-6 py-3 border-l-2 transition-colors ${
                  active
                    ? "border-[#FF3B30] bg-[#121212] text-white"
                    : "border-transparent text-[#A3A3A3] hover:bg-[#121212] hover:text-white"
                }`}
              >
                <Icon size={20} weight={active ? "fill" : "regular"} />
                <span className="font-heading uppercase tracking-wide font-semibold text-sm">{l.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-[#262626] p-4 space-y-2">
          <Link to="/profile" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white" data-testid="profile-link">
            <User size={16} />
            <span className="truncate">{user?.name}</span>
          </Link>
          <div className="flex items-center justify-between">
            <span className={`text-xs font-heading uppercase tracking-wider px-2 py-0.5 border ${
              user?.subscription_tier === "elite" ? "border-[#FF3B30] text-[#FF3B30]" :
              user?.subscription_tier === "pro" ? "border-[#34C759] text-[#34C759]" :
              user?.subscription_tier === "coach_pro" ? "border-[#FF9F0A] text-[#FF9F0A]" :
              user?.subscription_tier === "team" ? "border-[#BF5AF2] text-[#BF5AF2]" :
              "border-[#262626] text-[#A3A3A3]"
            }`} data-testid="subscription-badge">
              {user?.subscription_tier === "coach_pro" ? "COACH" : user?.subscription_tier === "team" ? "TEAM" : user?.subscription_tier?.toUpperCase() || "FREE"}
            </span>
            <button onClick={doLogout} className="text-[#A3A3A3] hover:text-[#FF3B30]" data-testid="logout-button">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        {children}
      </main>
    </div>
  );
}
